import { useState, useEffect, useCallback } from 'react';
import { notificationsApi } from '../services/api';

const usePushNotifications = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported] = useState(() => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSupported) return;

    // Check existing subscription without blocking
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg) {
        reg.pushManager.getSubscription().then(sub => {
          setIsSubscribed(!!sub);
        });
      }
    }).catch(() => {});
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setLoading(false);
        return false;
      }

      const { publicKey } = await notificationsApi.getVapidKey();

      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      await notificationsApi.subscribe(subscription.toJSON());
      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Subscribe error:', err);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const subscription = await reg.pushManager.getSubscription();
        if (subscription) {
          await notificationsApi.unsubscribe(subscription.endpoint);
          await subscription.unsubscribe();
        }
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error('Unsubscribe error:', err);
    }
    setLoading(false);
  }, []);

  return { isSupported, isSubscribed, loading, subscribe, unsubscribe };
};

export default usePushNotifications;
